import React, { useEffect, useState } from "react";

import {
    ResponsiveContainer,
    LineChart,
    Line,
    XAxis,
    YAxis,
    Tooltip,
    CartesianGrid,
    PieChart,
    Pie,
    Cell,
    BarChart,
    Bar,
    Legend
} from "recharts";

import {
    FaIndustry,
    FaLeaf,
    FaChartLine,
    FaArrowTrendUp
} from "react-icons/fa6";

import apiClient from "../../shared/API/apiClient";

import {
    useReportingYear
} from "../../context/ReportingYearContext";

const COLORS = [
    "#ef4444",
    "#2563eb",
    "#10b981",
    "#f59e0b",
    "#8b5cf6"
];

const ICONS = {
    scope1: FaIndustry,
    scope2: FaLeaf,
    total: FaChartLine,
    average: FaArrowTrendUp,
    peak: FaChartLine
};

const formatNumber = value => {

    if(
        value === null ||
        value === undefined ||
        value === ""
    ) {
        return "-";
    }

    return Number(value).toLocaleString(
        "en-IN",
        {
            maximumFractionDigits: 2
        }
    );
};

const EmissionTrendsPage = () => {

    const { reportingYear, reportingYearId } = useReportingYear();
    const [reportingYearInfo, setReportingYearInfo] =useState(null);
    const [loading, setLoading] = useState(false);
    const [report, setReport] = useState(null);
    const [legalEntities, setLegalEntities] = useState([]);
    const [selectedEntity, setSelectedEntity] = useState("");

    // =====================================
    // ENTITY
    // =====================================
useEffect(() => {

   const loadReportingYear = async () => {

    try {

        const res =
            await apiClient.post(
                "/api/reportingyear"
            );

        if (res.data.code === 200) {

            const selected =
                (res.data.data || [])
                    .find(
                        y =>
                            Number(y.id) ===
                            Number(reportingYearId)
                    );

            setReportingYearInfo(
                selected || null
            );
        }

    } catch (e) {

        console.error(
            "Reporting year API error:",
            e
        );
    }
};

    loadReportingYear();

}, [reportingYearId]);
    useEffect(() => {

        const load = async () => {

            if(!reportingYearId) {
                return;
            }

            try {

                const res =
                    await apiClient.get(
                        "/api/legal-entities-by-year",
                        {
                            params: {
                                reporting_year_id:
                                    reportingYearId
                            }
                        }
                    );

                setLegalEntities(
                    res.data.data || []
                );

            } catch {

                setLegalEntities([]);
            }
        };

        load();

    }, [reportingYearId]);

    // =====================================
    // FILTER
    // =====================================

    const [filters, setFilters] =
        useState({
            filter_type: "THIS_YEAR",
            quarter: "",
            month: "",
            from_date: "",
            to_date: ""
        });

      const handleFilterChange = (field, value) => {

    const updated = {
        ...filters,
        [field]: value
    };

    if(field === "filter_type") {

        updated.quarter = "";
        updated.month = "";

        if(value === "CUSTOM" && reportingYearInfo) {
            updated.from_date =
                reportingYearInfo.start_date;

            updated.to_date =
                reportingYearInfo.end_date;
        }

        if(value !== "CUSTOM") {
            updated.from_date = "";
            updated.to_date = "";
        }
    }

    setFilters(updated);
};

    // =====================================
    // LOAD
    // =====================================

    const loadReport = async () => {

        if(
            !reportingYearId ||
            !selectedEntity
        ) {
            return;
        }

        try {

            setLoading(true);

            const response =
            await apiClient.get(
                "/api/reports/emission-trends",
                {
                    params: {

                        reporting_year_id:
                            reportingYearId,

                        legal_entity_id:
                            selectedEntity,

                        ...filters
                    }
                }
            );

        setReport(response.data);

        } finally {

            setLoading(false);
        }
    };

    useEffect(() => {

        loadReport();

    }, [
        reportingYearId,
        selectedEntity
    ]);

    if(loading) {

        return (
            <div className="dash-loader-wrapper">
                <div className="dash-loader" />
            </div>
        );
    }
    return (

    <div className="dash-page">

        <div className="dash-page-title">
            Emission Trends
        </div>

        <div className="dash-page-subtitle">
            Greenhouse Gas Emissions, Source Distribution and Trend Analysis
        </div>

        {/* FILTER */}

        <div className="filter-card">

            

            <div className="filter-group">

                <label>
                    Legal Entity
                </label>

                <select
                    className="filter-fields"
                    value={selectedEntity}
                    onChange={e => setSelectedEntity(e.target.value)}
                >

                    <option value="">
                        Select
                    </option>

                    {
                        legalEntities.map(e => (
                            <option
                                key={e.entity_id}
                                value={e.entity_id}
                            >
                                {e.entity_name}
                            </option>
                        ))
                    }

                </select>

            </div>

            <div className="filter-group">

                    <label>Period</label>

                    <select
                        className="filter-fields"
                        value={filters.filter_type}
                        onChange={e => handleFilterChange("filter_type", e.target.value)}
                    >
                        <option value="THIS_YEAR">This Year</option>
                        <option value="THIS_QUARTER">Quarter</option>
                        <option value="THIS_MONTH">Month</option>
                        <option value="CUSTOM">Custom</option>
                    </select>

                </div>
    {
                    filters.filter_type === "THIS_QUARTER" && (
        <div className="filter-group">

            <label>Quarter</label>

            <select
                className="filter-fields"
                value={filters.quarter}
                onChange={e => handleFilterChange("quarter", e.target.value)}
            >
                <option value="">Select Quarter</option>
                <option value="1">Q1</option>
                <option value="2">Q2</option>
                <option value="3">Q3</option>
                <option value="4">Q4</option>
            </select>

        </div>
    )
}

{
    filters.filter_type === "THIS_MONTH" && (
        <div className="filter-group">

            <label>Month</label>

            <select
                className="filter-fields"
                value={filters.month}
                onChange={e => handleFilterChange("month", e.target.value)}
            >

                <option value="">Select Month</option>

                {
                    (() => {

                        const months = [];

                        const start = new Date(
                            report?.reporting_period?.from_date ||
                            `${new Date().getFullYear()}-04-01`
                        );

                        const current = new Date();
                        const temp = new Date(start);

                        while(
                            temp.getFullYear() < current.getFullYear() ||
                            (
                                temp.getFullYear() === current.getFullYear() &&
                                temp.getMonth() <= current.getMonth()
                            )
                        ) {

                            const year = temp.getFullYear();

                            const month = String(
                                temp.getMonth() + 1
                            ).padStart(2, "0");

                            months.push({
                                value: `${year}-${month}`,
                                label: temp.toLocaleString(
                                    "en-IN",
                                    {
                                        month: "short",
                                        year: "numeric"
                                    }
                                )
                            });

                            temp.setMonth(
                                temp.getMonth() + 1
                            );
                        }

                        return months.map(item => (
                            <option
                                key={item.value}
                                value={item.value}
                            >
                                {item.label}
                            </option>
                        ));
                    })()
                }

            </select>

        </div>
    )
}

{
    filters.filter_type === "CUSTOM" && (
        <>
            <div className="filter-group">

                <label>
                    From
                </label>

                <input
                    type="date"
                    className="filter-fields"
                    min={
                        reportingYearInfo?.start_date
                    }
                    max={
                        reportingYearInfo?.end_date
                    }
                    value={filters.from_date}
                    onChange={(e) =>
                        handleFilterChange(
                            "from_date",
                            e.target.value
                        )
                    }
        />

            </div>

            <div className="filter-group">

                <label>
                    To
                </label>

                <input
                        type="date"
                        className="filter-fields"
                        min={
                            reportingYearInfo?.start_date
                        }
                        max={
                            reportingYearInfo?.end_date
                        }
                        value={filters.to_date}
                        onChange={(e) =>
                            handleFilterChange(
                                "to_date",
                                e.target.value
                            )
                        }
                    />

            </div>
        </>
    )
}
                <div className="filter-group">

                    <label>&nbsp;</label>

                    <button className="org-btn-save" onClick={loadReport}>
                        Generate Report
                    </button>

                </div>

        </div>
        {
                !report && (

                    <div className="empty-entity">
          <div className="empty-entity__icon"><i className="fa-solid fa-building" /></div>
          <div className="empty-entity__title">Select a Legal Entity to continue</div>
          <div className="empty-entity__sub">
             Select a legal entity to analyze Scope 1, Scope 2 and total carbon emissions performance.
          </div>
          
          
        
        </div>
                )
            }

        {
            report &&(() => {

                const {
                    applied_filter,
                    kpis,
                    trend,
                    trend_config,
                    scope_distribution,
                    monthly_contribution,
                    table,
                    features
                } = report;

                return (

                    <>

                        <div className="reporting_year_line">
                            <strong>{applied_filter.label}</strong> | {applied_filter.from_date} to {applied_filter.to_date}
                        </div>
                        {/* KPI */}

                        <div className="dash-stat-grid" style={{
                                    gridTemplateColumns:
                                        `repeat(${kpis.length},1fr)`
                                }}>

                            {
                                kpis.map(kpi => {
                                    const Icon = ICONS[kpi.key];

                                    return (
                                        <div key={kpi.key} className="dash-stat-card">

                                            <div className="dash-stat-header">
                                                <Icon />
                                                <div className="dash-stat-label">{kpi.title}</div>
                                            </div>

                                            <div className="dash-stat-value">{kpi.value}</div>

                                            <div className="dash-stat-sub">{kpi.unit}</div>

                                        </div>
                                    );
                                })
                            }

                        </div>
                                                {/* TREND */}

                        {
                            features.show_trend && (
                                <div className="chart-card">

                                    <h4>Emission Trend</h4>

                                    <ResponsiveContainer width="100%" height={350}>
                                        <LineChart data={trend}>
                                            <CartesianGrid strokeDasharray="3 3" />
                                            <XAxis dataKey="period" />
                                            <YAxis />
                                            <Tooltip />
                                            <Legend />
                                            {
                                                trend_config.map(item => (
                                                    <Line
                                                        key={item.key}
                                                        type="monotone"
                                                        dataKey={item.key}
                                                        stroke={item.color}
                                                        strokeWidth={2}
                                                    />
                                                ))
                                            }
                                        </LineChart>
                                    </ResponsiveContainer>

                                </div>
                            )
                        }
                        <div className="chart-row">

                            {/* SCOPE */}

                            {
                                features.show_scope_distribution && (
                                    <div className="chart-card">

                                        <h4>Scope Distribution</h4>

                                        <ResponsiveContainer width="100%" height={300}>
                                            <PieChart>
                                                <Pie
                                                    data={scope_distribution}
                                                    dataKey="value"
                                                    nameKey="name"
                                                    outerRadius={100}
                                                    label
                                                >
                                                    {
                                                        scope_distribution.map((entry, index) => (
                                                            <Cell
                                                                key={index}
                                                                fill={COLORS[index % COLORS.length]}
                                                            />
                                                        ))
                                                    }
                                                </Pie>
                                                <Tooltip />
                                            </PieChart>
                                        </ResponsiveContainer>

                                    </div>
                                )
                            }

                            {/* MONTHLY */}

                            {
                                features.show_monthly_chart && (
                                    <div className="chart-card">

                                        <h4>
                                            Monthly Contribution
                                        </h4>

                                        <ResponsiveContainer width="100%" height={300}>
                                            <BarChart data={monthly_contribution}>
                                                <CartesianGrid strokeDasharray="3 3" />
                                                <XAxis dataKey="name" />
                                                <YAxis />
                                                <Tooltip />
                                                <Bar dataKey="value" fill="#10b981" />
                                            </BarChart>
                                        </ResponsiveContainer>

                                    </div>
                                )
                            }

                        </div>
                                                {/* TABLE */}

                        {
                            features.show_table && (
                                <div className="table-card">

                                    <h4>
                                        Emission Details
                                    </h4>

                                    <table className="table">

                                        <thead>
                                            <tr>
                                                <th>Period</th>
                                                <th>Scope 1</th>
                                                <th>Scope 2</th>
                                                <th>Total</th>
                                                <th>Growth %</th>
                                            </tr>
                                        </thead>

                                        <tbody>

    {
        table.map((row, index) => (
            <tr key={index}>
                <td>{row.period}</td>
                <td>{formatNumber(row.scope1)}</td>
                <td>{formatNumber(row.scope2)}</td>
                <td>{formatNumber(row.total)}</td>
                <td>{row.growth}</td>
            </tr>
        ))
    }

</tbody>

</table>

                                </div>
                            )
                        }

                    </>
                );
            })()
        }

    </div>
);
};

export default EmissionTrendsPage;